import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { DownloadCloud, Tag, FilePlus2, Link2, Search, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { DateInput } from "@/components/DateInput";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { mydataApi, mydataError } from "@/api";
import { useLang, fmtMoney, fmtDate } from "@/i18n";
import { MyDataBadge } from "@/components/MyDataBadge";

const PAGE = 50;

const IncomingRow = ({ inc, unlinked, onDone }) => {
  const { t, lang } = useLang();
  const [category, setCategory] = useState(inc.category || "goods");
  const [linkId, setLinkId] = useState("");
  const [busy, setBusy] = useState(false);

  const run = async (fn, okKey) => {
    setBusy(true);
    try { await fn(); toast.success(t(okKey)); onDone(); }
    catch (e) { toast.error(e?.response?.status === 502 ? `${t("mydata_error")}: ${e.response.data?.detail || ""}` : mydataError(e, t)); }
    finally { setBusy(false); }
  };

  return (
    <tr className="hover:bg-slate-50 dark:hover:bg-slate-800/40" data-testid={`incoming-row-${inc.id}`}>
      <td className="px-4 py-3 font-mono text-xs">{inc.mark}</td>
      <td className="px-4 py-3">
        <div className="font-medium">{inc.issuer_name || `${t("afm_short")} ${inc.issuer_afm}`}
          {inc.cancelled && <span className="ml-2 rounded bg-red-100 dark:bg-red-950 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-red-700 dark:text-red-300" data-testid={`incoming-cancelled-${inc.id}`}>{t("inc_cancelled")}</span>}
        </div>
        <div className="text-xs text-slate-500 font-mono">{t("afm_short")} {inc.issuer_afm} · {inc.series}-{inc.aa}</div>
      </td>
      <td className="px-4 py-3 font-mono text-slate-500 hidden md:table-cell">{fmtDate(inc.issue_date, lang)}</td>
      <td className="px-4 py-3 text-right font-mono">{fmtMoney(inc.total)}<div className="text-[10px] text-slate-500">{t("vat_amount")} {fmtMoney(inc.vat)}</div></td>
      <td className="px-4 py-3">
        <MyDataBadge mydata={inc} />
        {inc.document_id && <div className="mt-1"><Link to={`/documents/${inc.document_id}`} className="font-mono text-xs text-blue-800 dark:text-blue-300 hover:underline" data-testid={`incoming-doc-link-${inc.id}`}>{inc.document_number}</Link></div>}
      </td>
      <td className="px-4 py-3">
        {inc.cancelled ? <div className="text-xs text-slate-400 text-right">—</div> : inc.status === "classified" ? (
          <div className="text-xs text-slate-500 font-mono text-right">{inc.category ? t(`cat_${inc.category}`) : "—"}<br />{inc.classification_mark || ""}</div>
        ) : (
          <div className="flex flex-wrap justify-end gap-2">
            {inc.status === "new" && (
              <>
                <Button size="sm" variant="outline" disabled={busy} onClick={() => run(() => mydataApi.createFromIncoming(inc.id), "document_created")} data-testid={`incoming-create-${inc.id}`}>
                  <FilePlus2 size={13} className="mr-1" />{t("create_document")}
                </Button>
                {unlinked.length > 0 && (
                  <div className="flex gap-1">
                    <Select value={linkId} onValueChange={setLinkId}>
                      <SelectTrigger className="h-8 w-48 text-xs" data-testid={`incoming-link-select-${inc.id}`}><SelectValue placeholder={t("link_document")} /></SelectTrigger>
                      <SelectContent>{unlinked.map((d) => <SelectItem key={d.id} value={d.id}>{d.number} · {fmtMoney(d.total)}</SelectItem>)}</SelectContent>
                    </Select>
                    <Button size="sm" variant="outline" disabled={busy || !linkId} onClick={() => run(() => mydataApi.linkIncoming(inc.id, linkId), "linked_ok")} data-testid={`incoming-link-${inc.id}`}><Link2 size={13} /></Button>
                  </div>
                )}
              </>
            )}
            <div className="flex gap-1">
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger className="h-8 w-52 text-xs" data-testid={`incoming-category-${inc.id}`}><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="goods">{t("cat_goods")}</SelectItem>
                  <SelectItem value="services">{t("cat_services")}</SelectItem>
                  <SelectItem value="expenses">{t("cat_expenses")}</SelectItem>
                </SelectContent>
              </Select>
              <Button size="sm" disabled={busy} onClick={() => run(() => mydataApi.classifyIncoming(inc.id, category), "mydata_classified_ok")} className="bg-blue-800 hover:bg-blue-700" data-testid={`incoming-classify-${inc.id}`}>
                <Tag size={13} className="mr-1" />{t("mydata_classify")}
              </Button>
            </div>
          </div>
        )}
      </td>
    </tr>
  );
};

export const IncomingDocs = ({ onChanged }) => {
  const { t } = useLang();
  const [rows, setRows] = useState([]);
  const [unlinked, setUnlinked] = useState([]);
  const [status, setStatus] = useState("all");
  const [busy, setBusy] = useState(false);
  const [limit, setLimit] = useState(PAGE);
  const [q, setQ] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");

  useEffect(() => {
    setLimit(PAGE);
  }, [status, q, from, to]);

  const needle = q.trim().toLowerCase();
  const shown = rows.filter((r) => (!needle || (r.issuer_name || "").toLowerCase().includes(needle) || (r.issuer_afm || "").includes(needle))
    && (!from || r.issue_date >= from) && (!to || r.issue_date <= to));
  const filtered = needle || from || to;
  const clear = () => { setQ(""); setFrom(""); setTo(""); };

  const load = useCallback(() => {
    mydataApi.incoming(status).then(setRows).catch(() => toast.error(t("error")));
    mydataApi.unlinkedPurchases().then(setUnlinked);
  }, [status, t]);
  useEffect(load, [load]);

  const sync = async () => {
    setBusy(true);
    try {
      const r = await mydataApi.syncIncoming();
      toast.success(`${r.created} ${t("incoming_synced")} · ${r.matched} ${t("incoming_matched")}${r.cancelled ? ` · ${r.cancelled} ${t("inc_cancelled_n")}` : ""}`);
      load(); onChanged?.();
    } catch (e) { toast.error(mydataError(e, t)); onChanged?.(); } finally { setBusy(false); }
  };

  return (
    <div className="erp-card overflow-hidden" data-testid="incoming-docs-card">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 px-5 py-4 border-b border-slate-200 dark:border-slate-800">
        <div>
          <h3 className="font-heading font-semibold" data-testid="incoming-count">{t("incoming_docs")} · {filtered ? `${shown.length} / ${rows.length}` : rows.length}</h3>
          <p className="text-xs text-slate-500 mt-1">{t("incoming_hint")}</p>
        </div>
        <div className="flex gap-2">
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="w-40" data-testid="incoming-status-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("all_statuses_inc")}</SelectItem>
              {["new", "linked", "classified"].map((s) => <SelectItem key={s} value={s}>{t(`md_status_${s}`)}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button onClick={sync} disabled={busy} className="bg-blue-800 hover:bg-blue-700" data-testid="incoming-sync-button"><DownloadCloud size={15} className="mr-1" />{t("sync_incoming")}</Button>
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-3 px-5 py-3 border-b border-slate-200 dark:border-slate-800 bg-slate-50/60 dark:bg-slate-900/40">
        <div className="relative flex-1 min-w-[220px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("incoming_search_ph")} className="pl-9" data-testid="incoming-search-input" />
        </div>
        <div className="grid grid-cols-[auto_1fr] sm:flex items-center gap-2 text-xs text-slate-500 w-full sm:w-auto">
          <span>{t("date_from")}</span>
          <DateInput value={from} onChange={(e) => setFrom(e.target.value)} className="font-mono w-full sm:w-40" data-testid="incoming-date-from-input" />
          <span>{t("date_to")}</span>
          <DateInput value={to} min={from || undefined} onChange={(e) => setTo(e.target.value)} className="font-mono w-full sm:w-40" data-testid="incoming-date-to-input" />
        </div>
        {filtered && <Button size="sm" variant="ghost" onClick={clear} data-testid="incoming-clear-filters-button"><X size={14} className="mr-1" />{t("clear_filters")}</Button>}
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm erp-table">
          <thead>
            <tr className="text-left">
              <th className="px-4 py-2">{t("mark")}</th>
              <th className="px-4 py-2">{t("supplier")}</th>
              <th className="px-4 py-2 hidden md:table-cell">{t("date")}</th>
              <th className="px-4 py-2 text-right">{t("total")}</th>
              <th className="px-4 py-2">{t("status")}</th>
              <th className="px-4 py-2 text-right"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
            {shown.slice(0, limit).map((inc) => <IncomingRow key={inc.id} inc={inc} unlinked={unlinked} onDone={() => { load(); onChanged?.(); }} />)}
            {shown.length === 0 && <tr><td colSpan={6} className="px-4 py-10 text-center text-slate-500" data-testid="incoming-empty">{t("no_results")}</td></tr>}
          </tbody>
        </table>
      </div>
      {shown.length > limit && (
        <div className="flex items-center justify-center gap-3 border-t border-slate-200 dark:border-slate-800 px-4 py-3 text-xs text-slate-500">
          <span data-testid="incoming-shown-count">{limit} / {shown.length}</span>
          <Button size="sm" variant="outline" onClick={() => setLimit(limit + PAGE)} data-testid="incoming-show-more-button">{t("show_more")}</Button>
        </div>
      )}
    </div>
  );
};
